'use client'

import React from 'react'
import {
    Button,
    ButtonIconPosition,
    BaseButtonProps,
    ButtonVariants,
    LinkAsButton,
    TextPosition,
    LinkAsButtonProps,
} from '../../../atoms'
import { useContextMenuInteractionMode } from '../ContextMenuInteractionContext'

export interface ContextMenuItemProps
    extends Pick<BaseButtonProps, 'type' | 'loading' | 'form' | 'icon' | 'iconPosition'>,
        Pick<LinkAsButtonProps, 'target' | 'rel' | 'download' | 'href' | 'download'> {
    children: React.ReactNode
    disabled?: boolean
    buttonVariant?: ButtonVariants
    onClick?: () => void
}

/**
 * Row in a context menu. Omit `href` for actions (opens modal, runs command) — renders a `<button>`.
 * Pass `href` for navigation — renders `LinkAsButton`, which itself renders a single `next/link`
 * anchor, so client-side navigation and prefetch are automatic. Do NOT wrap `ContextMenuItem` in an
 * outer `next/link` — that produces nested anchors (invalid HTML, broken a11y).
 *
 * @example In-app route (client navigation is automatic)
 * ```tsx
 * <ContextMenuItem href="/account/service-points">Servicepunten</ContextMenuItem>
 * ```
 *
 * @example External or static URL
 * ```tsx
 * <ContextMenuItem href="https://example.com/faq" target="_blank" rel="noopener noreferrer">
 *     FAQ
 * </ContextMenuItem>
 * ```
 *
 * @example Action (modal, command)
 * ```tsx
 * <ContextMenuItem onClick={openModal}>Welcome</ContextMenuItem>
 * ```
 */
export const ContextMenuItem = ({
    children,
    buttonVariant = ButtonVariants.DEFAULT_CONTEXT_MENU_ITEM,
    icon,
    href,
    download,
    type,
    loading,
    form,
    ...rest
}: ContextMenuItemProps) => {
    const interactionMode = useContextMenuInteractionMode()
    const menuItemProps = interactionMode === 'menu' ? { role: 'menuitem' as const, tabIndex: -1 as const } : {}

    const content = (
        <>
            {!!icon && (
                <span
                    className={`absolute ${
                        !rest.iconPosition || rest.iconPosition === ButtonIconPosition.LEFT ? 'left-0' : 'right-0'
                    }`}
                >
                    {icon}
                </span>
            )}
            <span className="mx-2">{children}</span>
        </>
    )

    if (href) {
        return (
            <LinkAsButton
                className="context-menu__item"
                variant={buttonVariant}
                textPosition={TextPosition.LEFT}
                href={href}
                download={download}
                {...rest}
                {...menuItemProps}
            >
                {content}
            </LinkAsButton>
        )
    }

    return (
        <Button
            className="context-menu__item"
            variant={buttonVariant}
            textPosition={TextPosition.LEFT}
            type={type}
            loading={loading}
            form={form}
            {...rest}
            {...menuItemProps}
        >
            {content}
        </Button>
    )
}
